import { Injectable } from "@angular/core";
import { BehaviorSubject, Observable, fromEvent, timer } from "rxjs";
import { map, filter, debounceTime, distinctUntilChanged } from "rxjs/operators";

export interface AnalyticsEvent {
  id: string;
  name: string;
  category: string;
  action: string;
  label?: string;
  value?: number;
  timestamp: number;
  userId?: string;
  sessionId: string;
  page: string;
  properties?: { [key: string]: any };
}

export interface UserSession {
  id: string;
  startTime: number;
  endTime?: number;
  duration?: number;
  pageViews: number;
  events: number;
  userId?: string;
  userAgent: string;
  referrer: string;
  language: string;
  timezone: string;
  screen: {
    width: number;
    height: number;
    colorDepth: number;
  };
  viewport: {
    width: number;
    height: number;
  };
}

export interface PageView {
  id: string;
  url: string;
  title: string;
  timestamp: number;
  sessionId: string;
  userId?: string;
  referrer: string;
  duration?: number;
  scrollDepth: number;
  timeOnPage: number;
}

export interface AnalyticsConfig {
  enabled: boolean;
  autoTrack: boolean;
  sessionTimeout: number;
  maxEvents: number;
  batchSize: number;
  flushInterval: number;
  endpoint?: string;
  debug: boolean;
}

@Injectable({
  providedIn: "root",
})
export class AnalyticsService {
  private eventsSubject = new BehaviorSubject<AnalyticsEvent[]>([]);
  private currentSessionSubject = new BehaviorSubject<UserSession | null>(null);
  private pageViewsSubject = new BehaviorSubject<PageView[]>([]);

  private config: AnalyticsConfig = {
    enabled: true,
    autoTrack: true,
    sessionTimeout: 30 * 60 * 1000, // 30 minutos
    maxEvents: 10000,
    batchSize: 50,
    flushInterval: 30000, // 30 segundos
    debug: false,
  };

  private currentSession: UserSession | null = null;
  private eventQueue: AnalyticsEvent[] = [];
  private flushTimer?: any;
  private sessionTimer?: any;

  constructor() {
    this.initializeAnalytics();
  }

  /**
   * Inicializar analytics
   */
  private initializeAnalytics(): void {
    if (!this.config.enabled) {
      return;
    }

    this.startNewSession();
    this.setupAutoTracking();
    this.setupEventListeners();
    this.startFlushTimer();
  }

  /**
   * Iniciar nova sessão
   */
  private startNewSession(): void {
    const sessionId = this.generateId();
    const now = Date.now();

    this.currentSession = {
      id: sessionId,
      startTime: now,
      pageViews: 0,
      events: 0,
      userAgent: navigator.userAgent,
      referrer: document.referrer,
      language: navigator.language,
      timezone: Intl.DateTimeFormat().resolvedOptions().timeZone,
      screen: {
        width: screen.width,
        height: screen.height,
        colorDepth: screen.colorDepth,
      },
      viewport: {
        width: window.innerWidth,
        height: window.innerHeight,
      },
    };

    this.currentSessionSubject.next(this.currentSession);
    this.startSessionTimer();
  }

  /**
   * Configurar auto tracking
   */
  private setupAutoTracking(): void {
    if (!this.config.autoTrack) {
      return;
    }

    // Track page views
    this.trackPageView();

    // Track clicks
    fromEvent(document, "click")
      .pipe(debounceTime(100))
      .subscribe((event: Event) => {
        const target = event.target as HTMLElement;
        if (target) {
          this.trackEvent("click", "element", target.tagName, undefined, {
            id: target.id,
            className: target.className,
            text: target.textContent?.substring(0, 100),
          });
        }
      });

    // Track form submissions
    fromEvent(document, "submit").subscribe((event: Event) => {
      const form = event.target as HTMLFormElement;
      if (form) {
        this.trackEvent("form", "submit", form.id || form.className);
      }
    });

    // Track scroll depth
    fromEvent(window, "scroll")
      .pipe(debounceTime(100))
      .subscribe(() => {
        const scrollDepth = this.calculateScrollDepth();
        this.trackEvent("scroll", "depth", Math.round(scrollDepth).toString());
      });

    // Track time on page
    this.trackTimeOnPage();
  }

  /**
   * Configurar event listeners
   */
  private setupEventListeners(): void {
    // Track page visibility changes
    fromEvent(document, "visibilitychange").subscribe(() => {
      if (document.hidden) {
        this.trackEvent("page", "hidden");
      } else {
        this.trackEvent("page", "visible");
      }
    });

    // Track window resize
    fromEvent(window, "resize")
      .pipe(debounceTime(100))
      .subscribe(() => {
        this.trackEvent("window", "resize", `${window.innerWidth}x${window.innerHeight}`);
      });

    // Track beforeunload
    fromEvent(window, "beforeunload").subscribe(() => {
      this.endSession();
      this.flushEvents();
    });
  }

  /**
   * Iniciar timer de flush
   */
  private startFlushTimer(): void {
    this.flushTimer = setInterval(() => {
      this.flushEvents();
    }, this.config.flushInterval);
  }

  /**
   * Iniciar timer de sessão
   */
  private startSessionTimer(): void {
    this.sessionTimer = setTimeout(() => {
      this.endSession();
      this.startNewSession();
    }, this.config.sessionTimeout);
  }

  /**
   * Track evento
   */
  trackEvent(
    category: string,
    action: string,
    label?: string,
    value?: number,
    properties?: { [key: string]: any },
  ): void {
    if (!this.config.enabled || !this.currentSession) {
      return;
    }

    const event: AnalyticsEvent = {
      id: this.generateId(),
      name: `${category}_${action}`,
      category,
      action,
      label,
      value,
      timestamp: Date.now(),
      sessionId: this.currentSession.id,
      page: window.location.pathname,
      properties,
    };

    this.addEvent(event);
  }

  /**
   * Track page view
   */
  trackPageView(title?: string): void {
    if (!this.config.enabled || !this.currentSession) {
      return;
    }

    const pageView: PageView = {
      id: this.generateId(),
      url: window.location.href,
      title: title || document.title,
      timestamp: Date.now(),
      sessionId: this.currentSession.id,
      referrer: document.referrer,
      scrollDepth: 0,
      timeOnPage: 0,
    };

    this.addPageView(pageView);
    this.currentSession.pageViews++;
    this.currentSessionSubject.next(this.currentSession);
  }

  /**
   * Adicionar evento
   */
  private addEvent(event: AnalyticsEvent): void {
    this.eventQueue.push(event);
    this.eventsSubject.next([...this.eventQueue]);

    if (this.currentSession) {
      this.currentSession.events++;
      this.currentSessionSubject.next(this.currentSession);
    }

    // Flush se atingir batch size
    if (this.eventQueue.length >= this.config.batchSize) {
      this.flushEvents();
    }
  }

  /**
   * Adicionar page view
   */
  private addPageView(pageView: PageView): void {
    const currentPageViews = this.pageViewsSubject.value;
    const newPageViews = [...currentPageViews, pageView];

    // Limitar número de page views
    if (newPageViews.length > this.config.maxEvents) {
      newPageViews.splice(0, newPageViews.length - this.config.maxEvents);
    }

    this.pageViewsSubject.next(newPageViews);
  }

  /**
   * Calcular scroll depth
   */
  private calculateScrollDepth(): number {
    const scrollTop = window.pageYOffset || document.documentElement.scrollTop;
    const documentHeight = document.documentElement.scrollHeight - window.innerHeight;
    return documentHeight > 0 ? (scrollTop / documentHeight) * 100 : 0;
  }

  /**
   * Track time on page
   */
  private trackTimeOnPage(): void {
    const startTime = Date.now();

    setInterval(() => {
      const timeOnPage = Date.now() - startTime;
      this.trackEvent("page", "time", Math.round(timeOnPage / 1000).toString());
    }, 30000); // A cada 30 segundos
  }

  /**
   * Flush eventos
   */
  private async flushEvents(): Promise<void> {
    if (this.eventQueue.length === 0 || !this.config.endpoint) {
      return;
    }

    const eventsToSend = [...this.eventQueue];
    this.eventQueue = [];

    try {
      await fetch(this.config.endpoint, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify({
          events: eventsToSend,
          session: this.currentSession,
          timestamp: Date.now(),
        }),
      });
    } catch (error) {
      // Re-adicionar eventos à fila em caso de erro
      this.eventQueue.unshift(...eventsToSend);
    }
  }

  /**
   * Finalizar sessão
   */
  private endSession(): void {
    if (this.currentSession) {
      this.currentSession.endTime = Date.now();
      this.currentSession.duration = this.currentSession.endTime - this.currentSession.startTime;
      this.currentSessionSubject.next(this.currentSession);
    }
  }

  /**
   * Obter eventos
   */
  getEvents(): AnalyticsEvent[] {
    return this.eventsSubject.value;
  }

  /**
   * Obter observable dos eventos
   */
  getEvents$(): Observable<AnalyticsEvent[]> {
    return this.eventsSubject.asObservable();
  }

  /**
   * Obter sessão atual
   */
  getCurrentSession(): UserSession | null {
    return this.currentSession;
  }

  /**
   * Obter observable da sessão atual
   */
  getCurrentSession$(): Observable<UserSession | null> {
    return this.currentSessionSubject.asObservable();
  }

  /**
   * Obter page views
   */
  getPageViews(): PageView[] {
    return this.pageViewsSubject.value;
  }

  /**
   * Obter observable dos page views
   */
  getPageViews$(): Observable<PageView[]> {
    return this.pageViewsSubject.asObservable();
  }

  /**
   * Obter estatísticas
   */
  getStats(): any {
    const events = this.getEvents();
    const pageViews = this.getPageViews();
    const session = this.getCurrentSession();

    const eventCategories = [...new Set(events.map((e) => e.category))];
    const eventActions = [...new Set(events.map((e) => e.action))];

    return {
      totalEvents: events.length,
      totalPageViews: pageViews.length,
      eventCategories: eventCategories.length,
      eventActions: eventActions.length,
      currentSession: session,
      averageTimeOnPage:
        pageViews.length > 0 ? pageViews.reduce((sum, pv) => sum + pv.timeOnPage, 0) / pageViews.length : 0,
      topPages: this.getTopPages(),
      topEvents: this.getTopEvents(),
    };
  }

  /**
   * Obter páginas mais visitadas
   */
  private getTopPages(): Array<{ url: string; count: number }> {
    const pageViews = this.getPageViews();
    const pageCounts: { [key: string]: number } = {};

    pageViews.forEach((pv) => {
      pageCounts[pv.url] = (pageCounts[pv.url] || 0) + 1;
    });

    return Object.entries(pageCounts)
      .map(([url, count]) => ({ url, count }))
      .sort((a, b) => b.count - a.count)
      .slice(0, 10);
  }

  /**
   * Obter eventos mais frequentes
   */
  private getTopEvents(): Array<{ name: string; count: number }> {
    const events = this.getEvents();
    const eventCounts: { [key: string]: number } = {};

    events.forEach((event) => {
      eventCounts[event.name] = (eventCounts[event.name] || 0) + 1;
    });

    return Object.entries(eventCounts)
      .map(([name, count]) => ({ name, count }))
      .sort((a, b) => b.count - a.count)
      .slice(0, 10);
  }

  /**
   * Configurar analytics
   */
  configure(config: Partial<AnalyticsConfig>): void {
    this.config = { ...this.config, ...config };

    if (config.enabled !== undefined) {
      if (config.enabled) {
        this.initializeAnalytics();
      } else {
        this.stopAnalytics();
      }
    }
  }

  /**
   * Parar analytics
   */
  stopAnalytics(): void {
    if (this.flushTimer) {
      clearInterval(this.flushTimer);
    }

    if (this.sessionTimer) {
      clearTimeout(this.sessionTimer);
    }

    this.endSession();
    this.flushEvents();
  }

  /**
   * Limpar dados
   */
  clearData(): void {
    this.eventsSubject.next([]);
    this.pageViewsSubject.next([]);
    this.eventQueue = [];
  }

  /**
   * Gerar ID único
   */
  private generateId(): string {
    return `${Date.now()}-${Math.random().toString(36).substr(2, 9)}`;
  }
}
